import { Play, Pause, Music, VolumeX } from 'lucide-react';
import SafeImg from './SafeImg';

/**
 * Reusable square tile that displays a track's cover art with a centered
 * play/pause overlay. Used in:
 *   - LibraryPage track rows
 *   - CrateDetailPage track rows
 *   - PlaylistDetailPage / Sets track rows
 *   - DashboardPage AI-set widget
 *
 * Sizing comes from the parent via Tailwind classes on `wrapperClassName`
 * (default 56px). The play button auto-scales relative to the tile so
 * the visual ratio stays consistent.
 */
export default function TrackCoverButton({
  track,
  isCurrentlyPlaying = false,
  isPlaying = false,
  onPlayPause,
  wrapperClassName = 'w-14 h-14',
  // 'sm' = small button (good for compact rows), 'md' = bigger (dashboard)
  buttonSize = 'sm',
  testIdPrefix = 'play-track',
}) {
  const hasAudio = !!(track?.has_audio || track?.storage_path);
  const hasCover = !!track?.cover_url;

  const btnSizeCls = buttonSize === 'md'
    ? 'w-9 h-9'
    : 'w-7 h-7';
  const iconSize = buttonSize === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';

  return (
    <div className={`relative rounded-md overflow-hidden border border-[#E2E8F0] bg-[#F8FAFC] group/cover ${wrapperClassName}`}>
      {hasCover ? (
        <SafeImg
          src={track.cover_url}
          alt=""
          className="w-full h-full object-cover"
          iconClassName="w-1/2 h-1/2 text-[#94A3B8]"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF]">
          <Music className="w-5 h-5 text-[#94A3B8]" />
        </div>
      )}
      {/* Dim overlay on hover so the button stays legible on busy covers */}
      <div className={`absolute inset-0 transition-opacity ${
        hasCover
          ? 'bg-black/0 group-hover/cover:bg-black/35'
          : 'bg-black/0'
      }`} />
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onPlayPause?.(track);
        }}
        disabled={!hasAudio}
        className={`absolute inset-0 flex items-center justify-center transition-opacity ${
          isCurrentlyPlaying
            ? 'opacity-100'
            : hasCover
              ? 'opacity-0 group-hover/cover:opacity-100'
              : 'opacity-100'
        }`}
        data-testid={`${testIdPrefix}-${track?.id}`}
        title={hasAudio ? 'Play' : 'Audio not synced'}
      >
        {isCurrentlyPlaying && isPlaying ? (
          <Pause className={`${iconSize} text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]`} />
        ) : hasAudio ? (
          <Play className={`${iconSize} ml-0.5 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]`} />
        ) : (
          <VolumeX className={`${iconSize} text-white/50`} />
        )}
      </button>
    </div>
  );
}
